import * as Sentry from '@sentry/react'

// PII fields that must never leave the user's machine. Session Replay
// already masks all text + inputs by default; this list belongs to the
// beforeSend hook so it also scrubs values that show up in error contexts
// (e.g. an exception thrown while serializing a sales payload).
const PII_KEYS = new Set([
  'customer_name',
  'notes',
  'description',
  'invite_email',
  'note',
])

function scrubPII<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map(scrubPII) as unknown as T
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = PII_KEYS.has(k) ? '[scrubbed]' : scrubPII(v)
  }
  return out as unknown as T
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    // Local dev without a DSN configured — no-op so console isn't noisy.
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Source maps uploaded via @sentry/vite-plugin in a follow-up commit
    // (needs the auth token from User Settings → Auth Tokens).
    release: import.meta.env.VITE_RELEASE_SHA,

    integrations: [
      Sentry.replayIntegration({
        // Defaults already mask all text + all inputs. Keep those on.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],

    // Replays: sample 10% of normal sessions, 100% of sessions that hit
    // an error so we always have a video for bug reports.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Tracing: 10% in prod, 100% in dev so the local app gets full coverage.
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

    // Scrub PII out of contexts/extras before the event leaves the browser.
    beforeSend(event) {
      if (event.contexts) event.contexts = scrubPII(event.contexts)
      if (event.extra) event.extra = scrubPII(event.extra)
      if (event.request?.data) event.request.data = scrubPII(event.request.data)
      return event
    },
  })
}
